import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import h2p from "html2plaintext";
import htmlParser from "html-react-parser";
import { BsCheck2 } from "react-icons/bs";
import { FiShoppingBag, FiMaximize2, FiX, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { AiFillHeart, AiOutlineHeart } from "react-icons/ai";
import { useDispatch, useSelector } from "react-redux";
import DetailsImage from "./DetailsImage";
import { addCart, incQuantity, decQuantity, removeItem } from "../../store/reducers/cartReducer";
import { imageUrl } from "../../utils/imageUrl";
import { getFinalPrice } from "../../utils/productPrice";
import { toman, tomanParts, toPersianNumber } from "../../utils/persian";
import { getColorCode } from "../../utils/colorCodes";
import { useFeedback } from "../../context/AppFeedbackContext";
import {
  useAddWishlistMutation,
  useGetWishlistQuery,
  useRemoveWishlistMutation,
} from "../../store/services/authService";
import { isLocalWishlistItem, readLocalWishlist, writeLocalWishlist } from "../../utils/wishlistStorage";
import { usePaymentSettingsQuery } from "../../store/services/paymentService";



const snappPayLogo = "/images/payment/snapp-pay/40x40@3x.png";

const normalizeOption = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value.name || value.title || value.value || value.label || "";
};

const normalizeOptions = (items) => {
  if (!Array.isArray(items)) return [];
  return items.map(normalizeOption).filter(Boolean);
};

const toFaText = (value = "") =>
  String(value ?? "").replace(/[0-9]/g, (digit) => "۰۱۲۳۴۵۶۷۸۹"[Number(digit)]);

const isSameCartItem = (item, payload) => {
  const itemVariantId = item.variant_id || item.product_variant_id || "";
  const payloadVariantId = payload.variant_id || payload.product_variant_id || "";

  if (itemVariantId || payloadVariantId) {
    return String(itemVariantId) === String(payloadVariantId);
  }

  return (
    String(item._id) === String(payload._id) &&
    (item.color || "") === (payload.color || "") &&
    (item.size || "") === (payload.size || "")
  );
};

const normalizeAttributes = (items) => {
  if (!Array.isArray(items)) return [];

  return items
    .map((item) => {
      if (!item) return null;

      if (typeof item === "string") {
        return { name: "ویژگی", value: item };
      }

      const name = item.name || item.title || item.label || item.key || "";
      const value = item.value || item.description || item.text || "";

      if (!name && !value) return null;

      return { name, value };
    })
    .filter(Boolean);
};



const galleryItemPath = (item) => {
  if (!item) return "";
  if (typeof item === "string") return item;
  return item.path || item.url || item.image || item.src || "";
};

const galleryItemColor = (item) => {
  if (!item || typeof item === "string") return "";
  return normalizeOption(item.color);
};

const getVariantImage = (variant) => {
  if (!variant) return "";

  return variant.image || variant.image1 || variant.main_image || variant.photo || variant.thumbnail || "";
};



const DetailsCard = ({ product }) => {
  const variants = useMemo(() => (Array.isArray(product?.variants) ? product.variants : []), [product?.variants]);

  const activeVariants = useMemo(
    () => variants.filter((item) => item?.is_active !== false),
    [variants]
  );

  const sizes = useMemo(
    () =>
      activeVariants.length
        ? [...new Set(activeVariants.map((item) => normalizeOption(item.size)).filter(Boolean))]
        : normalizeOptions(product?.sizes),
    [activeVariants, product?.sizes]
  );

  const colors = useMemo(
    () =>
      activeVariants.length
        ? [...new Set(activeVariants.map((item) => normalizeOption(item.color)).filter(Boolean))]
        : normalizeOptions(product?.colors),
    [activeVariants, product?.colors]
  );

  const attributes = useMemo(() => normalizeAttributes(product?.attributes), [product?.attributes]);

  const galleryItems = useMemo(() => (Array.isArray(product?.gallery) ? product.gallery : []), [product]);

  const images = useMemo(() => {
    const normalizedGallery = galleryItems
      .map(galleryItemPath)
      .filter(Boolean);

    return [
      product?.main_image || product?.image1 || product?.image,
      ...normalizedGallery,
      product?.image2,
      product?.image3
    ]
      .filter(Boolean)
      .filter((item, index, array) => array.indexOf(item) === index);
  }, [product, galleryItems]);

  const [activeImage, setActiveImage] = useState(images[0] || "");
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    setActiveImage(images[0] || "");
  }, [images]);

  const [sizeState, setSizeState] = useState("");
  const [colorState, setColorState] = useState(colors.length > 0 ? colors[0] : "");
  const [quantity, setQuantity] = useState(1);
  const [isWishlisted, setIsWishlisted] = useState(false);

  const inStockVariants = useMemo(
    () => activeVariants.filter((variant) => Number(variant?.stock || 0) > 0),
    [activeVariants]
  );

  const availableSizes = useMemo(() => {
    if (!sizes.length) return [];

    if (!activeVariants.length) {
      return Number(product?.stock || 0) > 0 ? sizes : [];
    }

    return sizes.filter((size) =>
      inStockVariants.some(
        (variant) => normalizeOption(variant.size) === size
      )
    );
  }, [sizes, activeVariants, inStockVariants, product?.stock]);

  const availableColors = useMemo(() => {
    if (!colors.length) return [];

    if (!activeVariants.length) {
      return Number(product?.stock || 0) > 0 ? colors : [];
    }

    return colors.filter((color) =>
      inStockVariants.some(
        (variant) => normalizeOption(variant.color) === color
      )
    );
  }, [colors, activeVariants, inStockVariants, product?.stock]);

  const sizesForSelectedColor = useMemo(() => {
    if (!sizes.length) return [];

    if (!activeVariants.length) {
      return Number(product?.stock || 0) > 0 ? sizes : [];
    }

    if (!colorState) return [];

    return sizes.filter((size) =>
      inStockVariants.some(
        (variant) =>
          normalizeOption(variant.color) === colorState &&
          normalizeOption(variant.size) === size
      )
    );
  }, [
    sizes,
    activeVariants,
    inStockVariants,
    colorState,
    product?.stock,
  ]);

  useEffect(() => {
    if (!activeVariants.length) {
      if (!colorState && availableColors.length) {
        setColorState(availableColors[0]);
      }

      if (!sizeState && availableSizes.length) {
        setSizeState(availableSizes[0]);
      }

      return;
    }

    if (!inStockVariants.length) {
      if (colorState) setColorState("");
      if (sizeState) setSizeState("");
      setQuantity(1);
      return;
    }

    const selectedColorAvailable = availableColors.includes(colorState);

    if (!selectedColorAvailable) {
      const firstVariant = inStockVariants[0];

      setColorState(normalizeOption(firstVariant.color));

      if (sizes.length) {
        setSizeState(normalizeOption(firstVariant.size));
      }

      setQuantity(1);
      return;
    }

    if (
      sizes.length &&
      (!sizeState || !sizesForSelectedColor.includes(sizeState))
    ) {
      setSizeState(sizesForSelectedColor[0] || "");
      setQuantity(1);
    }
  }, [
    activeVariants,
    inStockVariants,
    availableColors,
    availableSizes,
    sizesForSelectedColor,
    colors,
    sizes,
    colorState,
    sizeState,
  ]);

  const selectColor = (color) => {
    if (!availableColors.includes(color)) return;

    if (!activeVariants.length) {
      setColorState(color);

      if (!sizeState && availableSizes.length) {
        setSizeState(availableSizes[0]);
      }

      setQuantity(1);
      return;
    }

    const matchingVariants = inStockVariants.filter(
      (variant) => normalizeOption(variant.color) === color
    );

    if (!matchingVariants.length) return;

    const currentSizeVariant = matchingVariants.find(
      (variant) => normalizeOption(variant.size) === sizeState
    );

    const nextVariant = currentSizeVariant || matchingVariants[0];

    setColorState(color);

    if (sizes.length) {
      setSizeState(normalizeOption(nextVariant.size));
    }

    setQuantity(1);
  };

  const selectSize = (size) => {
    if (!activeVariants.length) {
      if (!availableSizes.includes(size)) return;

      setSizeState(size);
      setQuantity(1);
      return;
    }

    const exactVariant = inStockVariants.find(
      (variant) =>
        normalizeOption(variant.color) === colorState &&
        normalizeOption(variant.size) === size
    );

    if (!exactVariant) return;

    setSizeState(size);
    setQuantity(1);
  };

  const dispatch = useDispatch();
  const { notify } = useFeedback();
  const { cart } = useSelector((state) => state.cartReducer);
  const { userToken } = useSelector((state) => state.authReducer);
  const { data: wishlistData } = useGetWishlistQuery(undefined, { skip: !userToken });
  const [addWishlist] = useAddWishlistMutation();
  const [removeWishlist] = useRemoveWishlistMutation();
  const paymentSettingsResponse = usePaymentSettingsQuery();
  const snappPayEnabled = Boolean(paymentSettingsResponse.data?.snapp_pay?.enabled);

  const price = Number(product?.price || 0);
  const campaignSalePrice = product?.campaign_sale_price ? Number(product.campaign_sale_price) : 0;
  const apiDiscount = Number(product?.discount || product?.discount_percent || 0);
  const finalPrice = getFinalPrice(product);
  const calculatedDiscount = price > 0 && finalPrice > 0 && finalPrice < price ? Math.round((1 - finalPrice / price) * 100) : 0;
  const discountPercent = calculatedDiscount || apiDiscount;
  const isCampaignSale = Boolean(campaignSalePrice);
  const productId = product?._id || product?.id;
  const productTitle = product?.title || product?.name || "محصول";
  const productImage = images[0] || product?.image1 || product?.main_image || product?.image || product?.gallery?.[0] || "";
  const hasDiscount = price > 0 && finalPrice > 0 && finalPrice < price;
  const wishlistImage = typeof productImage === "string" ? productImage : galleryItemPath(productImage);
  const snappInstallmentAmount = Math.ceil(finalPrice / 4);
  const snappPayTitle = `هر قسط با اسنپ‌پی ${toman(snappInstallmentAmount)}`;
  const snappPayDescription = "۴ قسط ماهانه بدون سود، چک و ضامن";
  const shouldShowSnappPay = snappPayEnabled && finalPrice > 0;



  useEffect(() => {
    if (!productId) return;

    if (userToken) {
      const items = wishlistData?.items || [];
      setIsWishlisted(items.some((item) => String(item.product_id) === String(productId)));
      return;
    }

    setIsWishlisted(isLocalWishlistItem(readLocalWishlist(), productId));
  }, [productId, userToken, wishlistData]);

  const toggleWishlist = async () => {
    if (!productId) {
      notify("شناسه محصول معتبر نیست.", "error", "خطا");
      return;
    }

    if (userToken) {
      try {
        if (isWishlisted) {
          await removeWishlist(productId).unwrap();
          setIsWishlisted(false);
          notify("محصول از علاقه‌مندی‌ها حذف شد.", "success", "علاقه‌مندی‌ها");
          return;
        }

        await addWishlist(productId).unwrap();
        setIsWishlisted(true);
        notify("محصول به علاقه‌مندی‌ها اضافه شد.", "success", "علاقه‌مندی‌ها");
      } catch (err) {
        notify(err?.data?.message || "تغییر علاقه‌مندی انجام نشد.", "error", "علاقه‌مندی‌ها");
      }

      return;
    }

    const items = readLocalWishlist();

    if (isLocalWishlistItem(items, productId)) {
      const nextItems = items.filter((item) => String(item.id) !== String(productId));
      writeLocalWishlist(nextItems);
      setIsWishlisted(false);
      notify("محصول از علاقه‌مندی‌ها حذف شد.", "success", "علاقه‌مندی‌ها");
      return;
    }

    writeLocalWishlist([
      {
        id: productId,
        product_id: productId,
        name: productTitle,
        title: productTitle,
        price,
        sale_price: finalPrice,
        image: wishlistImage,
        main_image: wishlistImage,
      },
      ...items,
    ]);

    setIsWishlisted(true);
    notify("محصول به علاقه‌مندی‌ها اضافه شد.", "success", "علاقه‌مندی‌ها");
  };

  const finalPriceParts = tomanParts(finalPrice);
  const basePriceParts = tomanParts(price);

  const selectedVariant = useMemo(() => {
    if (!activeVariants.length) return null;

    return activeVariants.find((variant) => {
      const sameColor = colors.length ? normalizeOption(variant.color) === colorState : true;
      const sameSize = sizes.length ? normalizeOption(variant.size) === sizeState : true;
      return sameColor && sameSize;
    }) || null;
  }, [activeVariants, colorState, sizeState, colors.length, sizes.length]);

  const selectedVariantImage = getVariantImage(selectedVariant);

  const selectedGalleryImage = useMemo(() => {
    if (!colorState) return "";

    const matched = galleryItems.find((item) => galleryItemColor(item) === colorState);

    return galleryItemPath(matched);
  }, [galleryItems, colorState]);

  useEffect(() => {
    if (selectedVariantImage) {
      setActiveImage(selectedVariantImage);
      return;
    }

    if (selectedGalleryImage) {
      setActiveImage(selectedGalleryImage);
    }
  }, [selectedVariantImage, selectedGalleryImage]);

  const currentImageIndex = Math.max(images.findIndex((item) => item === activeImage), 0);

  const openLightbox = () => {
    if (activeImage) {
      setLightboxOpen(true);
    }
  };

  const changeLightboxImage = (direction) => {
    if (!images.length) return;

    const nextIndex = direction === "next"
      ? (currentImageIndex + 1) % images.length
      : (currentImageIndex - 1 + images.length) % images.length;

    setActiveImage(images[nextIndex]);
  };

  const selectedStock = activeVariants.length
    ? selectedVariant
      ? Number(selectedVariant.stock || 0)
      : 0
    : Number(product?.stock || 0);
  const selectedCartPayload = {
    _id: productId,
    variant_id: selectedVariant?.id || null,
    product_variant_id: selectedVariant?.id || null,
    color: colorState,
    size: sizeState
  };
  const existingCartItem = cart.find((item) => isSameCartItem(item, selectedCartPayload));
  const currentCartQuantity = Number(existingCartItem?.quantity || 0);
  const remainingStock = Math.max(selectedStock - currentCartQuantity, 0);
  const hasSelectedStock = selectedStock > 0;
  const isAllSelectedStockInCart = hasSelectedStock && currentCartQuantity >= selectedStock;
  const isSelectedAvailable = hasSelectedStock && remainingStock > 0;

  const cartActionPayload = {
    _id: productId,
    variant_id: selectedVariant?.id || null,
    product_variant_id: selectedVariant?.id || null,
    color: colorState,
    size: sizeState
  };

  const increaseCartQuantity = () => {
    if (!existingCartItem) return;

    if (currentCartQuantity >= selectedStock) {
      notify("حداکثر موجودی این محصول در سبد شما قرار دارد.", "warning", "موجودی محصول");
      return;
    }

    dispatch(incQuantity(cartActionPayload));
    notify("تعداد محصول در سبد افزایش یافت.", "success", "سبد خرید");
  };

  const decreaseCartQuantity = () => {
    if (!existingCartItem) return;

    if (currentCartQuantity <= 1) {
      removeFromCart();
      return;
    }

    dispatch(decQuantity(cartActionPayload));
    notify("تعداد محصول در سبد کاهش یافت.", "success", "سبد خرید");
  };

  const removeFromCart = () => {
    if (!existingCartItem) return;

    dispatch(removeItem(cartActionPayload));

    notify("این انتخاب از سبد خرید حذف شد.", "success", "سبد خرید");
  };

  const addToCart = () => {
    if (!productId) {
      notify("شناسه محصول معتبر نیست.", "error", "خطا")
      return;
    }

    if (sizes.length > 0 && !sizeState) {
      notify("لطفاً سایز را انتخاب کنید.", "warning", "انتخاب سایز")
      return;
    }

    if (colors.length > 0 && !colorState) {
      notify("لطفاً رنگ را انتخاب کنید.", "warning", "انتخاب رنگ")
      return;
    }

    if (!isSelectedAvailable) {
      notify(currentCartQuantity >= selectedStock ? "حداکثر موجودی این محصول در سبد شما قرار دارد." : "این ترکیب رنگ و سایز موجود نیست.", "warning", "موجودی محصول")
      return;
    }

    dispatch(
      addCart({
        _id: productId,
        id: productId,
        variant_id: selectedVariant?.id || null,
        product_variant_id: selectedVariant?.id || null,
        sku: selectedVariant?.sku || null,
        title: productTitle,
        name: productTitle,
        price,
        sale_price: finalPrice,
        campaign_sale_price: campaignSalePrice || null,
        discount: discountPercent,
        image: productImage,
        image1: productImage,
        main_image: productImage,
        color: colorState,
        size: sizeState,
        stock: selectedStock,
        available_stock: selectedStock,
        quantity: Math.min(quantity, remainingStock)
      })
    );

    window.dispatchEvent(new CustomEvent("jooti:cart-added"));
  };

  const readableDescription = product?.description || product?.seo_text || product?.seo_description || "";
  let desc = h2p(readableDescription || "");
  desc = htmlParser(desc);

  return (
    <motion.section initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="product-details">
      <div className="product-gallery-card">
        <div className="product-main-image">
          <button
            type="button"
            className={`product-wishlist-btn ${isWishlisted ? "active" : ""}`}
            onClick={toggleWishlist}
            aria-label={isWishlisted ? "حذف از علاقه‌مندی‌ها" : "افزودن به علاقه‌مندی‌ها"}
            title={isWishlisted ? "حذف از علاقه‌مندی‌ها" : "افزودن به علاقه‌مندی‌ها"}
          >
            {isWishlisted ? <AiFillHeart size={23} /> : <AiOutlineHeart size={23} />}
          </button>
          <button type="button" className="product-main-image-button" onClick={openLightbox} aria-label={`مشاهده تصویر بزرگ ${productTitle}`}>
            {activeImage ? (
              <img src={imageUrl(activeImage)} alt={`${productTitle} | جوتی`} loading="eager" fetchPriority="high" decoding="async" />
            ) : (
              <div className="product-image-placeholder">تصویر محصول</div>
            )}

            {activeImage && (
              <span className="product-image-zoom-badge">
                <FiMaximize2 size={15} />
                مشاهده عکس
              </span>
            )}
          </button>

          {colorState && (
            <span className="product-image-color-badge">
              <i style={{ background: getColorCode(colorState) }} />
              {colorState}
            </span>
          )}

          {!hasSelectedStock && (
            <span className="product-detail-stock-badge">ناموجود</span>
          )}

          {isAllSelectedStockInCart && (
            <span className="product-detail-stock-badge in-cart">در سبد شماست</span>
          )}

          {hasDiscount && hasSelectedStock && (
            <span className={isCampaignSale ? "product-detail-discount campaign" : "product-detail-discount"}>
              {toPersianNumber(discountPercent)}٪ تخفیف
            </span>
          )}
        </div>

        {images.length > 1 && (
          <div className="product-thumbs">
            {images.map((image) => (
              <DetailsImage
                image={image}
                key={image}
                active={activeImage === image}
                onClick={() => setActiveImage(image)}
              />
            ))}
          </div>
        )}
      </div>

      <div className="product-detail-card">
        <div className="product-detail-head">
          <span className="product-detail-category">{product?.category || "محصول جوتی"}</span>
          <h1>{productTitle}</h1>
          {product?.short_description && <p>{product.short_description}</p>}
        </div>

        <div className={isCampaignSale ? "product-detail-price campaign" : "product-detail-price"}>
          {isCampaignSale && hasSelectedStock && <span className="product-detail-campaign-label">فروش ویژه کمپین</span>}
          {hasSelectedStock ? (
            <>
              <div className="product-detail-modern-price">
                <strong>{finalPriceParts.amount}</strong>
                <span>{finalPriceParts.currency}</span>
              </div>

              {hasDiscount && (
                <del className="product-detail-old-price">
                  {basePriceParts.amount}
                </del>
              )}
            </>
          ) : (
            <>
              <strong className="product-detail-unavailable-price">ناموجود</strong>
              {finalPrice > 0 && <small>{toman(finalPrice)}</small>}
            </>
          )}
        </div>

        {shouldShowSnappPay && hasSelectedStock && (
          <div className="jooti-snapp-component product-snapp-component">
            <img src={snappPayLogo} alt="اسنپ‌پی" />
            <div>
              <strong>{snappPayTitle}</strong>
              <span>{snappPayDescription}</span>
            </div>
          </div>
        )}

        {sizes.length > 0 && (
          <div className="product-option-block">
            <div className="product-option-title">
              <h3>انتخاب سایز</h3>
              {sizeState && <span>سایز {toFaText(sizeState)}</span>}
            </div>

            <div className="product-size-list">
              {sizes.map((size) => {
                const sizeAvailable =
                  sizesForSelectedColor.includes(size);

                return (
                  <button
                    type="button"
                    key={size}
                    onClick={() => selectSize(size)}
                    disabled={!sizeAvailable}
                    aria-disabled={!sizeAvailable}
                    title={
                      !sizeAvailable && colorState
                        ? `سایز ${toFaText(size)} در رنگ ${colorState} موجود نیست`
                        : ""
                    }
                    className={`${sizeState === size ? "active" : ""} ${
                      !sizeAvailable ? "disabled" : ""
                    }`}
                  >
                    {toFaText(size)}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {colors.length > 0 && (
          <div className="product-option-block">
            <div className="product-option-title">
              <h3>انتخاب رنگ</h3>
              {colorState && <span>{colorState}</span>}
            </div>

            <div className="product-color-list">
              {colors.map((color) => {
                const colorAvailable =
                  availableColors.includes(color);

                return (
                  <button
                    type="button"
                    key={color}
                    onClick={() => selectColor(color)}
                    disabled={!colorAvailable}
                    aria-disabled={!colorAvailable}
                    title={!colorAvailable ? "این رنگ فعلاً ناموجود است" : ""}
                    className={`${colorState === color ? "active" : ""} ${
                      !colorAvailable ? "disabled" : ""
                    }`}
                  >
                    <i style={{ background: getColorCode(color) }} />
                    <span>{color}</span>
                    {colorState === color && <BsCheck2 size={17} />}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className={`product-stock-hint ${!hasSelectedStock ? "danger-state" : isAllSelectedStockInCart ? "cart-state" : ""}`}>
          {hasSelectedStock ? (
            existingCartItem ? (
              isAllSelectedStockInCart ? (
                <span>همه موجودی این انتخاب در سبد شماست</span>
              ) : (
                <span>در سبد خرید؛ {toPersianNumber(remainingStock)} عدد دیگر قابل افزودن است</span>
              )
            ) : remainingStock === 1 ? (
              <span>آخرین عدد موجود</span>
            ) : (
              <span>موجود</span>
            )
          ) : (
            <span className="danger">ناموجود</span>
          )}
        </div>

        <div className={`product-buy-box ${existingCartItem ? "in-cart" : ""}`}>
          {existingCartItem ? (
            <div className="product-cart-inline-controls">
              <button type="button" onClick={decreaseCartQuantity} aria-label="کاهش تعداد">
                −
              </button>

              <div className="product-cart-inline-count">
                <strong>{toPersianNumber(currentCartQuantity)}</strong>
                <span>در سبد</span>
              </div>

              <button
                type="button"
                onClick={increaseCartQuantity}
                disabled={isAllSelectedStockInCart}
                aria-label="افزایش تعداد"
                title={isAllSelectedStockInCart ? "همه موجودی این انتخاب در سبد شماست" : "افزایش تعداد"}
              >
                +
              </button>

              <Link to="/cart" className="product-view-cart-link">
                مشاهده سبد
              </Link>
            </div>
          ) : (
            <button className="product-add-cart-btn" onClick={addToCart} disabled={!isSelectedAvailable}>
              <FiShoppingBag size={19} />
              {isSelectedAvailable ? "افزودن به سبد خرید" : "ناموجود"}
            </button>
          )}
        </div>

      </div>

      {(attributes.length > 0 || readableDescription) && (
        <div className="product-details-info-card">
          {attributes.length > 0 && (
            <div className="product-attributes-card product-attributes-table-card">
              <div className="product-section-head product-section-head-clean">
                <span>مشخصات محصول</span>
              </div>

              <div className="product-attributes-table">
                {attributes.map((item, index) => (
                  <div className="product-attribute-row" key={`${item.name}-${index}`}>
                    <span>{item.name}</span>
                    <strong>{item.value}</strong>
                  </div>
                ))}
              </div>
            </div>
          )}

          {readableDescription && (
            <div className="product-description-card">
              <div className="product-section-head product-section-head-clean">
                <span>درباره این محصول</span>
              </div>
              <div className="description">{desc}</div>
            </div>
          )}
        </div>
      )}

      {lightboxOpen && (
        <div className="product-image-lightbox" role="dialog" aria-modal="true" onClick={() => setLightboxOpen(false)}>
          <div className="product-lightbox-content" onClick={(event) => event.stopPropagation()}>
            <div
              className="product-lightbox-image-wrap"
              style={{ "--lightbox-image": `url(${imageUrl(activeImage)})` }}
            >
              <button type="button" className="product-lightbox-close" onClick={() => setLightboxOpen(false)} aria-label="بستن تصویر">
                <FiX size={21} />
              </button>

              {images.length > 1 && (
                <button type="button" className="product-lightbox-arrow product-lightbox-arrow-right" onClick={(event) => {
                  event.stopPropagation();
                  changeLightboxImage("prev");
                }} aria-label="تصویر قبلی">
                  <FiChevronRight size={25} />
                </button>
              )}

              {colorState && (
                <span className="product-lightbox-color-badge">
                  <i style={{ background: getColorCode(colorState) }} />
                  {colorState}
                </span>
              )}

              <img key={activeImage} src={imageUrl(activeImage)} alt={`${productTitle} | جوتی`} loading="eager" fetchPriority="high" decoding="async" />

              {images.length > 1 && (
                <button type="button" className="product-lightbox-arrow product-lightbox-arrow-left" onClick={(event) => {
                  event.stopPropagation();
                  changeLightboxImage("next");
                }} aria-label="تصویر بعدی">
                  <FiChevronLeft size={25} />
                </button>
              )}
            </div>

            {images.length > 1 && (
              <div className="product-lightbox-thumbs">
                {images.map((image) => (
                  <button
                    type="button"
                    key={image}
                    className={activeImage === image ? "active" : ""}
                    onClick={() => setActiveImage(image)}
                    aria-label="انتخاب تصویر محصول"
                  >
                    <img src={imageUrl(image)} alt={`${productTitle} | جوتی`} loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </motion.section>
  );
};

export default DetailsCard;
